import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAgency } from "@/contexts/AgencyContext";
import { getAgencyLogoUrl } from "@/utils/agencyLogo";
import AuthPageBackground from "@/components/ui/shared/AuthPageBackground";

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("auth");
  const { agency } = useAgency();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const logoSrc = getAgencyLogoUrl(agency, true, "/logo-white.png");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!email) {
      setError(t("forgot.missingEmail"));
      return;
    }

    if (!agency) {
      setError(t("forgot.agencyNotIdentified"));
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/forgot-password`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email,
            domain: agency.domain,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        const errorMsg = result.error || t("forgot.sendError");
        const details = result.details ? `\n\nDetalhes: ${result.details}` : "";
        setError(errorMsg + details);
        return;
      }

      setSuccess(true);
    } catch (err: any) {
      console.error("Erro ao solicitar recuperação:", err);
      setError(t("forgot.connectionError"));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="w-full h-full flex justify-center items-center relative">
        <AuthPageBackground />
        <div className="w-full gap-4 flex flex-col max-w-[425px] text-neutral-50">
          <div className="w-full gap-6 flex flex-col bg-neutral-600 rounded-md p-6">
            <img
              src={logoSrc}
              className="h-[57px] max-w-[180px] self-center object-contain"
              alt={agency?.company_name || t("common.logoAlt")}
              onError={(e) => {
                e.currentTarget.src = "/logo-white.png";
              }}
            />
            <div className="p-4 bg-green-500/20 border border-green-500/30 rounded-md">
              <p className="text-green-300 text-sm text-center font-semibold">
                {t("forgot.sentTitle")}
              </p>
              <p className="text-neutral-300 text-xs text-center mt-2">
                {t("forgot.sentBody")}
              </p>
              <p className="text-neutral-400 text-xs text-center mt-3">
                {t("forgot.sentHint")}
              </p>
            </div>
            <Button
              onClick={() => navigate("/login")}
              variant="primary"
              className="mt-4"
            >
              {t("common.backToLogin")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex justify-center items-center relative">
      <AuthPageBackground />
      <div className="w-full gap-4 flex flex-col max-w-[425px] text-neutral-50">
        <div className="w-full gap-6 flex flex-col bg-neutral-600 rounded-md p-6">
          <img
            src={logoSrc}
            className="h-[57px] max-w-[180px] self-center object-contain"
            alt={agency?.company_name || t("common.logoAlt")}
            onError={(e) => {
              e.currentTarget.src = "/logo-white.png";
            }}
          />
          <div>
            <h2 className="text-xl font-semibold mb-2">{t("forgot.title")}</h2>
            <p className="text-sm text-neutral-300">
              {t("forgot.description")}
            </p>
          </div>
          {error && (
            <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-md">
              <p className="text-red-300 text-sm whitespace-pre-line">{error}</p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="form-group flex flex-col gap-2">
              <label htmlFor="email" className="text-sm">
                {t("common.email")}
              </label>
              <Input
                type="email"
                id="email"
                placeholder={t("common.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="mt-4" disabled={loading}>
              {loading ? t("forgot.submitting") : t("forgot.submit")}
            </Button>
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="text-sm font-light text-neutral-300 hover:text-neutral-50 transition-colors"
            >
              {t("common.backToLogin")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
